import { enqueue } from 'api/transcription-api';
import * as WebsocketAPI from 'api/websocket-api';
export class WebsocketUpload {
    constructor(connection, project, withDraft = false) {
        this.ws = null;
        this.fileOffset = 0;
        this.maxBufferSize = 1000000;
        this.isReady = false;
        this.numChannels = 1;
        this.bytesPerSample = 2;
        this.connection = connection;
        this.project = project;
        this.withDraft = withDraft;
    }
    setFormat(bytesPerSample, numChannels) {
        this.bytesPerSample = bytesPerSample;
        this.numChannels = numChannels;
    }
    async connect() {
        this.ws = WebsocketAPI.openUploadSocket(this.project.id.toString());
        await new Promise((resolve) => {
            if (this.ws !== null) {
                this.ws.onopen = () => {
                    var _a;
                    global.logger.info('UploadWebSocket opened');
                    this.isReady = true;
                    const meta = {
                        FileName: 'audio.wav',
                        TotalFileSize: null,
                        CurrentFileOffset: 0,
                    };
                    (_a = this.ws) === null || _a === void 0 ? void 0 : _a.send(JSON.stringify(meta));
                    resolve();
                };
            }
        });
        this.ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if ((data === null || data === void 0 ? void 0 : data.BufferSize) !== undefined) {
                    this.maxBufferSize = data.BufferSize;
                }
            }
            catch (_a) {
                global.logger.info('UploadWebSocket received non-JSON message', { data: event.data });
            }
        };
        this.ws.onerror = (event) => {
            global.logger.info('UploadWebSocket error', { event });
        };
        this.ws.onclose = (event) => {
            global.logger.info('UploadWebSocket closed', {
                code: event.code,
                reason: event.reason,
            });
            this.isReady = false;
        };
    }
    sendRawAudioChunk(chunk) {
        if (!this.isReady) {
            return;
        }
        if (chunk.byteLength >= this.maxBufferSize) {
            throw new Error(`Chunk too large: ${chunk.byteLength} >= ${this.maxBufferSize}`);
        }
        this.sendBinary(chunk);
    }
    async startTranscription(header, language) {
        var _a, _b, _c, _d, _e, _f;
        this.sendBinary(header);
        await this.enqueueTranscription(language, (_b = (_a = this.project.transcriptionConfig) === null || _a === void 0 ? void 0 : _a.profile) !== null && _b !== void 0 ? _b : 'default', (_d = (_c = this.project.transcriptionConfig) === null || _c === void 0 ? void 0 : _c.withPPC) !== null && _d !== void 0 ? _d : true, (_f = (_e = this.project.transcriptionConfig) === null || _e === void 0 ? void 0 : _e.withDiarization) !== null && _f !== void 0 ? _f : false);
    }
    close() {
        var _a;
        (_a = this.ws) === null || _a === void 0 ? void 0 : _a.close();
        this.ws = null;
        this.isReady = false;
    }
    sendEmptyChunk(frames) {
        let byteLength = this.getSilenceByteLength(frames);
        if (byteLength <= 0) {
            return;
        }
        if (byteLength >= this.maxBufferSize) {
            const bytesPerFrame = this.getSilenceByteLength(1);
            const safeFrames = Math.floor((this.maxBufferSize - 1) / bytesPerFrame);
            byteLength = Math.max(0, this.getSilenceByteLength(safeFrames));
            if (byteLength === 0) {
                return;
            }
        }
        this.sendBinary(new ArrayBuffer(byteLength));
    }
    sendBinary(chunk) {
        if (this.ws === null) {
            return;
        }
        if (this.ws.readyState !== WebSocket.OPEN) {
            return;
        }
        const offsetBuf = new ArrayBuffer(8);
        new DataView(offsetBuf).setFloat64(0, this.fileOffset, true);
        const lengthBuf = new ArrayBuffer(2);
        new DataView(lengthBuf).setInt16(0, chunk.byteLength, true);
        const fullChunk = new Uint8Array(8 + 2 + chunk.byteLength);
        fullChunk.set(new Uint8Array(offsetBuf), 0);
        fullChunk.set(new Uint8Array(lengthBuf), 8);
        fullChunk.set(new Uint8Array(chunk), 10);
        try {
            this.ws.send(fullChunk);
            this.fileOffset += chunk.byteLength;
        }
        catch (e) {
            global.logger.error('UploadWebSocket failed to send binary chunk', { error: e.message });
        }
    }
    async enqueueTranscription(language, transcriptionProfile, withPPC, withDiarization) {
        if (language === null) {
            throw new Error('Cannot enqueue transcription: projectId or language is missing');
        }
        const result = await enqueue(this.connection, this.project.id, language, transcriptionProfile, withPPC, true, // withVAD - prevents hallucination during silence/muted mic
        withDiarization, false, // withUserLex
        true, // saveTrsx
        false, // withSpeakerId
        this.withDraft, true);
        if (result.isFail()) {
            throw new Error(`Failed to enqueue transcription: ${result.err()}`);
        }
    }
    getSilenceByteLength(frames) {
        const bytesPerSample = Math.ceil(this.bytesPerSample);
        const bytesPerFrame = bytesPerSample * this.numChannels;
        return frames * bytesPerFrame;
    }
}
